
import { APP_MESSAGES, DATE_FORMATS, REQ_ENCRYPT_PK, REQ_ENCRYPT_VERSION, TOAST_AUTO_CLOSE_TIMER } from "src/shared/config/src";
import { AxiosError } from 'axios';
import CryptoJS from 'crypto-js';
import { toast } from 'react-toastify';
import { AppRoutes, EymemoryRoutes } from "../navigation/routes";
import { useEffect, useState } from "react";
import { dateFormatter } from "src/shared/utils/src";
import { EYENGAGE_URL_CONSTANTS, URL_CONSTANTS } from "../config/appConstants";
import { ChildrenStoreData, LoginStorageData, Notification } from "src/shared/types/src";
import { eymemoryLogout, logout, updateCurrentChild } from "src/shared/redux-state/src";
import { store } from "../services/store";
import { matchPath } from "react-router-dom";

type TryCatchConfig = {
    onSuccess?: (result: any) => void;
    onError?: (error: any) => void;
    onFinally?: () => void;
};

export const getRequestSignature = async (body: Object): Promise<{ signature: string, timestamp: number }> => {
    const currentTimestamp = new Date().getTime();
    const message = `${REQ_ENCRYPT_VERSION}:${currentTimestamp}:${JSON.stringify(body)}`;
    const signature = CryptoJS.HmacSHA256(message, REQ_ENCRYPT_PK).toString(CryptoJS.enc.Hex);
    return Promise.resolve({ signature: signature, timestamp: currentTimestamp });
}

export const handleError = async (error: unknown, options?: {
    orderHash?: string
    }) => {
    let message = APP_MESSAGES.generalErrorMessage;

    if (typeof error === 'string') {
        // Handle string errors
        message = error;
    } else if (error instanceof AxiosError) {
        // Handle Axios errors
        if (error.response) {
            // Server responded with a status other than 2xx
            const { data } = error.response;
            message = data?.errors ? data.errors.join(', ') : data?.message || message;
            const status = error.response?.status;
            if (status === 401 || status === 403) {
                await store.dispatch(eymemoryLogout({orderHash: options?.orderHash}));
            }
        } else if (error.request) {
            // Request was made but no response received
            message = 'No response received from the server.';
        } else {
            // Something happened in setting up the request
            message = error?.message || APP_MESSAGES.generalErrorMessage;
        }
    } else if (error instanceof Error) {
        // Handle generic errors
        message = error?.message || APP_MESSAGES.generalErrorMessage;
    }

    // Show the error message using toast
    toast.error(message, { autoClose: TOAST_AUTO_CLOSE_TIMER });
};

export const navigateToDownloadAppScreen = () => {
    window.location.href = `${window.location.origin}${AppRoutes.MOBILE_APP}`
}

export const getFileName = (url: string) => {
    if (!url) return null;
    const regex = /[^\/]+$/;
    const fileName = url.match(regex)?.[0];
    return fileName || url;
};

export function tryCatchWrapper<T>(
    fn: () => T | Promise<T>,
    config: TryCatchConfig = {}
): Promise<T> {
    const { onSuccess, onError, onFinally } = config;

    return new Promise<T>(async (resolve) => {
        try {
            const result = await fn();
            if (onSuccess) onSuccess(result);
            resolve(result);
        } catch (error: any) {
            if (onError) 
                return onError(error);
            
            handleError(error);
        } finally {
            if (onFinally) onFinally();
        }
    });
}

export const useWindowWidth = () => {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return width;
};

export const getQueryString = (filters: any) => {

    const arr = []

    if (filters?.from && dateFormatter(filters.from, DATE_FORMATS.YMD)) arr.push(`${URL_CONSTANTS.FROM}=${dateFormatter(filters.from, DATE_FORMATS.YMD)}`)
    if (filters?.to && dateFormatter(filters.to, DATE_FORMATS.YMD)) arr.push(`${URL_CONSTANTS.TO}=${dateFormatter(filters.to, DATE_FORMATS.YMD)}`)
    if (filters?.activityType?.length) arr.push(`${URL_CONSTANTS.ACTIVITY_TYPE}=${filters.activityType.join(",")}`)
    if (filters?.showFutureEvents && Boolean(filters?.showFutureEvents)) arr.push(`${URL_CONSTANTS.FUTURE_EVENTS}=${filters.showFutureEvents}`)
    if (filters?.postType) arr.push(`${EYENGAGE_URL_CONSTANTS.TYPE}=${filters?.postType}`)
    if (filters?.categoryIds) arr.push(`${EYENGAGE_URL_CONSTANTS.CATEGORIES}=${filters?.categoryIds}`)

    return arr.join('&');
}
export const getRedirectionRoute = (previousRoute?: string | null) => {
    let redirectionRoute = '';
    const searchParams = new URLSearchParams(window.location.search.slice(1));
    const redirectToFromUrl: any = searchParams.get(URL_CONSTANTS.REDIRECT_TO) ? searchParams.get(URL_CONSTANTS.REDIRECT_TO) : ''
    if (redirectToFromUrl) {
        redirectionRoute = redirectToFromUrl;
    } else if (previousRoute) {
        redirectionRoute = previousRoute
    }
    return redirectionRoute;
}

/**
 * Generates a temporary filename based on the provided file URL.
 * @param {string} fileURL - The URL of the file to download.
 * @returns {string} - The temporary file path with the generated name.
 */
const createTemporaryFileName = (fileURL: string, contentType?: string) => {
    fileURL = fileURL?.split('?')[0];
    let fileExtension = fileURL?.split('.').pop() || 'tmp';
    if (contentType === 'application/pdf')
        fileExtension = 'pdf';
  
    const timestamp = new Date().getTime();
    return `eyparent_${timestamp}.${fileExtension}`;
};

export const downloadFile = async (fileUrl: string, fileName?: string, onFinally?: () => void) => {
    let newFileName: string = fileName || '';
    if(!fileName) {
        newFileName = createTemporaryFileName(fileUrl);
    }
    await tryCatchWrapper(async () => {
        const response = await fetch(fileUrl, {
            method: 'GET', headers: {
                'Origin': window.location.origin, // Ensures CORS headers are sent
            }
        }); if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.setAttribute('download', newFileName); // Set the filename
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link); // Cleanup
        window.URL.revokeObjectURL(url); // Free memory
    }, {
        onFinally: () => {
            onFinally && onFinally();
        }
    });
};

export const getFileSize = (size: number) => {
    if (size < 1000000) {
        return `${(size / 1024).toFixed(2)} KB`
    } else {
        return `${(size / 1048576).toFixed(2)} MB`
    }
}

export const checkIsEylogOnlyNursery = (currentChild: ChildrenStoreData) => {
    return (
        Boolean(currentChild?.systemsEnabled?.eylog) &&
        Boolean(currentChild?.systemAccess?.eylog) &&
        !Boolean(currentChild?.systemsEnabled?.eymanage) &&
        !Boolean(currentChild?.systemAccess?.eymanage)
    )
}

export const switchCurrentChildIfNeeded = async (notification: Notification, currentChild: ChildrenStoreData, loginData: LoginStorageData, dispatch: any) => {
    let canNavigate = true;
    if (!currentChild?.notificationKey?.includes(notification.notificationKey)) {
        const updatedChild: ChildrenStoreData | undefined = loginData?.children?.find(
            (child) => child?.notificationKey?.includes(notification.notificationKey)
        );
        if (updatedChild) {
            await dispatch(
                updateCurrentChild({ currentChild: updatedChild })
            );
        } else {
            canNavigate = false;
            toast.error(APP_MESSAGES.childAssociatedWithThisNotificationNotFound);
        }
    }
    return canNavigate;
};

export const extractOrderHashFromPath = (pathname: string): string | null => {
    const orderHashPaths = [
        EymemoryRoutes.VALIDATE_EYMEMORIES_DOWNLOAD_LINK,
        `${EymemoryRoutes.EYMEMORIES_DOWNLOAD_FROM_LINK}/:orderHash`,
    ]
  for (const path of orderHashPaths) {
    const match = matchPath({ path, end: false }, pathname);
    if (match?.params?.orderHash) {
      return match.params.orderHash;
    }
  }
  return null;
};

export const encodeStringForQuery = (q: string) => {
    if (!q) {
        return '';
    }

    const base64Q = btoa(unescape(encodeURIComponent(q)));

    return encodeURIComponent(base64Q);
};

export const getParamsFromQuery = () => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (!q) return null;
    const decodedParams = new URLSearchParams(decodeURIComponent(escape(atob(q))));
    return decodedParams;
};